import { useEffect, useState, useCallback } from "react";
import { http, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Sparkle, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const ACTION_META = {
  BUY:   { color: "text-emerald-300", bg: "bg-emerald-500/15 border-emerald-700/40", Icon: TrendUp },
  ADD:   { color: "text-emerald-300", bg: "bg-emerald-500/15 border-emerald-700/40", Icon: TrendUp },
  SELL:  { color: "text-red-300",     bg: "bg-red-500/15 border-red-700/40",     Icon: TrendDown },
  TRIM:  { color: "text-amber-300",   bg: "bg-amber-500/15 border-amber-700/40", Icon: TrendDown },
  HOLD:  { color: "text-zinc-300",    bg: "bg-zinc-500/15 border-zinc-700/40",   Icon: Minus },
};

export default function Recommendations() {
  const [reco, setReco] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await http.get("/ai/recommendations/latest");
      if (data && data.summary) setReco(data);
    } catch (err) { console.error("latest reco fetch failed", err); }
    try {
      const { data } = await http.get("/ai/status");
      setStatus(data);
    } catch (err) { console.error("ai status fetch failed", err); }
  }, []);
  useEffect(() => { loadLatest(); }, [loadLatest]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await http.post("/ai/recommend");
      setReco(data);
      toast.success("Fresh recommendations generated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-bold">AI Advisor</div>
          <h1 className="mt-2 font-display text-4xl font-light">Personalised recommendations</h1>
          <p className="text-zinc-400 mt-2 text-sm">
            Powered by{" "}
            {status === null ? (
              <span className="text-zinc-500 animate-pulse" data-testid="ai-provider-label">Loading provider…</span>
            ) : status?.bedrock_configured ? (
              <span className="text-emerald-400" data-testid="ai-provider-label">
                AWS Bedrock · {status.bedrock_model?.split(".").pop()?.slice(0, 20) || "Claude"} ({status.bedrock_region})
              </span>
            ) : (
              <span className="text-amber-400" data-testid="ai-provider-label">
                AI not configured — set AWS Bedrock keys in .env
              </span>
            )}
          </p>
        </div>
        <Button onClick={generate} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5" data-testid="generate-reco-button">
          <Sparkle size={16} weight="fill" className="mr-2" />
          {loading ? "Analysing…" : reco ? "Regenerate" : "Generate advice"}
        </Button>
      </div>

      {!reco ? (
        <div className="bg-zinc-900 border border-dashed border-zinc-800 rounded-lg p-16 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400">
            <Sparkle size={26} weight="fill" />
          </div>
          <div className="mt-6 font-display text-xl">No recommendations yet</div>
          <p className="mt-2 text-sm text-zinc-500 max-w-md mx-auto">
            Add at least one holding and set your investing style, then click <span className="text-emerald-400">Generate advice</span>.
          </p>
        </div>
      ) : (
        <>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Overall summary</div>
            <p className="mt-3 text-zinc-200 leading-relaxed" data-testid="reco-summary">{reco.summary}</p>
            {reco.generated_at && (
              <div className="mt-4 text-xs text-zinc-500 font-mono">
                Generated {new Date(reco.generated_at).toLocaleString("en-IN")}
              </div>
            )}
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(reco.recommendations || []).map((r, i) => {
              const meta = ACTION_META[r.action] || ACTION_META.HOLD;
              const Icon = meta.Icon;
              return (
                <motion.div key={`${r.symbol}-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg p-5"
                  data-testid={`reco-card-${r.symbol}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-lg">{r.symbol}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        Confidence: <span className="text-zinc-300 capitalize">{r.confidence || "medium"}</span>
                        {r.target_weight_pct != null && <> · Target {r.target_weight_pct}%</>}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${meta.bg} ${meta.color} text-xs font-medium`}>
                      <Icon size={12} weight="bold" /> {r.action}
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{r.reason}</p>
                </motion.div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
